import { useState, useEffect, useMemo } from 'react';

interface DiaryEntry {
  id: string;
  date: string;
  title: string;
  content: string;
  mood: string;
  tags: string[];
  luckType: '길몽' | '흉몽' | '보통';
  createdAt: string;
}

const STORAGE_KEY = 'destiny_dream_diary';

function loadEntries(): DiaryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as DiaryEntry[]) : [];
  } catch {
    return [];
  }
}

const LUCK_CONFIG = {
  길몽: { color: 'text-amber-400', bg: 'bg-amber-500', light: 'bg-amber-500/20 border-amber-500/30', icon: 'ri-sun-fill' },
  흉몽: { color: 'text-red-400', bg: 'bg-red-500', light: 'bg-red-500/20 border-red-500/30', icon: 'ri-thunder-storms-fill' },
  보통: { color: 'text-purple-300', bg: 'bg-purple-500', light: 'bg-purple-500/20 border-purple-500/30', icon: 'ri-moon-fill' },
};

const MONTHS_KO = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

function getHeatmapColor(count: number): string {
  if (count === 0) return 'bg-white/5';
  if (count === 1) return 'bg-primary-gold/20';
  if (count === 2) return 'bg-primary-gold/40';
  if (count === 3) return 'bg-primary-gold/60';
  return 'bg-primary-gold/90';
}

export default function DreamStats() {
  const [entries, setEntries] = useState<DiaryEntry[]>([]);

  useEffect(() => {
    setEntries(loadEntries());
  }, []);

  const stats = useMemo(() => {
    if (entries.length === 0) return null;

    const total = entries.length;
    const luckCount = { 길몽: 0, 흉몽: 0, 보통: 0 };
    entries.forEach((e) => {
      luckCount[e.luckType] = (luckCount[e.luckType] || 0) + 1;
    });

    // Frequent tags
    const tagCount: Record<string, number> = {};
    entries.forEach((e) => {
      e.tags.forEach((tag) => {
        tagCount[tag] = (tagCount[tag] || 0) + 1;
      });
    });
    const topTags = Object.entries(tagCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);

    // Monthly heatmap (last 12 months)
    const now = new Date();
    const monthMap: Record<string, number> = {};
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthMap[key] = 0;
    }
    entries.forEach((e) => {
      const key = e.date.slice(0, 7);
      if (key in monthMap) monthMap[key]++;
    });
    const monthlyData = Object.entries(monthMap).map(([ym, count]) => {
      const [y, m] = ym.split('-');
      return { ym, label: MONTHS_KO[parseInt(m, 10) - 1], year: y, count };
    });

    // Weekly heatmap (last 7 weeks)
    const dayMap: Record<string, number> = {};
    for (let i = 48; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      dayMap[key] = 0;
    }
    entries.forEach((e) => {
      if (e.date in dayMap) dayMap[e.date]++;
    });
    const weeklyData = Object.entries(dayMap).map(([date, count]) => ({ date, count }));

    const streakDates = entries.map((e) => e.date).sort();
    let currentStreak = 0;
    if (streakDates.length > 0) {
      const today = new Date().toISOString().split('T')[0];
      let check = today;
      for (let i = 0; i < 30; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = d.toISOString().split('T')[0];
        if (streakDates.includes(key)) {
          currentStreak++;
        } else if (i > 0) {
          break;
        }
      }
    }

    return { total, luckCount, topTags, monthlyData, weeklyData, currentStreak };
  }, [entries]);

  if (entries.length === 0) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="text-center py-10">
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-gold/10 border border-primary-gold/20 mx-auto mb-3">
            <i className="ri-bar-chart-grouped-line text-primary-gold text-2xl"></i>
          </div>
          <p className="text-purple-300/50 text-sm">꿈 일기를 기록하면 통계 대시보드가 활성화됩니다</p>
        </div>
      </section>
    );
  }

  const maxTagCount = stats?.topTags[0]?.[1] || 1;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
      {/* Section header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-7 h-7 flex items-center justify-center">
          <i className="ri-bar-chart-grouped-line text-primary-gold text-xl"></i>
        </div>
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">꿈 통계 대시보드</h2>
          <p className="text-purple-300/60 text-sm">총 {stats?.total}개 꿈 기록 분석</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="space-y-5">
          {/* Luck distribution donut-style */}
          <div className="p-5 rounded-2xl bg-gradient-to-br from-primary-dark/80 to-mystic-dark/80 border border-primary-gold/20">
            <p className="text-white font-bold text-sm mb-4 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-pie-chart-2-fill text-primary-gold text-sm"></i>
              </div>
              길흉 비율
            </p>
            <div className="space-y-3">
              {(['길몽', '흉몽', '보통'] as const).map((type) => {
                const count = stats?.luckCount[type] || 0;
                const pct = stats ? Math.round((count / stats.total) * 100) : 0;
                const cfg = LUCK_CONFIG[type];
                return (
                  <div key={type}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${cfg.icon} ${cfg.color} text-xs`}></i>
                        </div>
                        <span className={`text-xs font-semibold ${cfg.color}`}>{type}</span>
                      </div>
                      <span className="text-white text-xs font-bold">{count}개 ({pct}%)</span>
                    </div>
                    <div className="h-2.5 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${cfg.bg} opacity-80`}
                        style={{ width: `${pct}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Visual circle representation */}
            <div className="mt-4 flex items-center justify-center gap-6">
              {(['길몽', '흉몽', '보통'] as const).map((type) => {
                const count = stats?.luckCount[type] || 0;
                const pct = stats ? Math.round((count / stats.total) * 100) : 0;
                const cfg = LUCK_CONFIG[type];
                const size = 40 + pct * 0.5;
                return (
                  <div key={type} className="flex flex-col items-center gap-1">
                    <div
                      className={`rounded-full border-2 flex items-center justify-center ${cfg.light} border-current transition-all duration-700`}
                      style={{ width: size, height: size }}
                    >
                      <span className={`font-bold ${cfg.color}`} style={{ fontSize: Math.max(10, size * 0.28) }}>
                        {pct}%
                      </span>
                    </div>
                    <span className={`text-[10px] font-semibold ${cfg.color}`}>{type}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Streak + Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-4 rounded-2xl bg-gradient-to-br from-amber-900/30 to-amber-800/20 border border-amber-500/25 text-center">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                <i className="ri-fire-fill text-amber-400 text-lg"></i>
              </div>
              <p className="text-amber-300 text-xl font-black">{stats?.currentStreak || 0}</p>
              <p className="text-amber-400/60 text-[10px]">연속 기록일</p>
            </div>
            <div className="p-4 rounded-2xl bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border border-purple-500/25 text-center">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                <i className="ri-book-2-fill text-purple-300 text-lg"></i>
              </div>
              <p className="text-purple-200 text-xl font-black">{stats?.total || 0}</p>
              <p className="text-purple-400/60 text-[10px]">총 꿈 기록</p>
            </div>
          </div>
        </div>

        {/* Middle column — tag frequency */}
        <div className="p-5 rounded-2xl bg-gradient-to-br from-primary-dark/80 to-mystic-dark/80 border border-primary-gold/20">
          <p className="text-white font-bold text-sm mb-4 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-hashtag text-primary-gold text-sm"></i>
            </div>
            자주 나오는 꿈 태그
          </p>
          {stats && stats.topTags.length > 0 ? (
            <div className="space-y-3">
              {stats.topTags.map(([tag, count], idx) => {
                const pct = Math.round((count / maxTagCount) * 100);
                const opacity = 1 - idx * 0.08;
                return (
                  <div key={tag}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-purple-200 text-xs font-medium">#{tag}</span>
                      <span className="text-primary-gold text-xs font-bold">{count}회</span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all duration-700"
                        style={{ width: `${pct}%`, opacity }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-purple-400/50 text-sm text-center py-8">태그를 추가해서 꿈 패턴을 분석해보세요</p>
          )}

          {/* Top tag insight */}
          {stats && stats.topTags.length > 0 && (
            <div className="mt-4 p-3 rounded-xl bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border border-indigo-500/20">
              <p className="text-indigo-300 text-xs font-semibold mb-1">꿈 패턴 인사이트</p>
              <p className="text-purple-200/70 text-xs leading-relaxed">
                가장 자주 등장하는 &quot;{stats.topTags[0][0]}&quot; 키워드는 당신의 잠재의식이 이 테마에 강한 에너지를 집중하고 있다는 신호입니다.
              </p>
            </div>
          )}
        </div>

        {/* Right column — monthly heatmap */}
        <div className="p-5 rounded-2xl bg-gradient-to-br from-primary-dark/80 to-mystic-dark/80 border border-primary-gold/20">
          <p className="text-white font-bold text-sm mb-4 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-calendar-check-fill text-primary-gold text-sm"></i>
            </div>
            월별 꿈 기록 히트맵
          </p>

          {/* Monthly bar chart */}
          {stats && (
            <div className="mb-5">
              <div className="flex items-end gap-1 h-24">
                {stats.monthlyData.map(({ ym, label, count }) => {
                  const maxMonthly = Math.max(...stats.monthlyData.map((m) => m.count), 1);
                  const heightPct = count === 0 ? 4 : Math.round((count / maxMonthly) * 100);
                  return (
                    <div key={ym} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full flex flex-col justify-end" style={{ height: '80px' }}>
                        <div
                          className={`w-full rounded-sm transition-all duration-700 ${count > 0 ? 'bg-gradient-to-t from-amber-500 to-amber-400' : 'bg-white/8'}`}
                          style={{ height: `${heightPct}%`, minHeight: '3px' }}
                          title={`${label}: ${count}개`}
                        ></div>
                      </div>
                      <span className="text-[8px] text-purple-400/60 leading-none">{label.replace('월', '')}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[9px] text-purple-400/50">{stats.monthlyData[0]?.year}년</span>
                <span className="text-[9px] text-purple-400/50">최근 12개월</span>
              </div>
            </div>
          )}

          {/* Weekly heatmap grid */}
          <p className="text-purple-400/60 text-xs mb-2.5">최근 49일 기록 히트맵</p>
          {stats && (
            <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {stats.weeklyData.map(({ date, count }) => (
                <div
                  key={date}
                  className={`aspect-square rounded-sm ${getHeatmapColor(count)} transition-all duration-300 cursor-default`}
                  title={`${date}: ${count}개`}
                ></div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between mt-2">
            <span className="text-[9px] text-purple-400/50">적음</span>
            <div className="flex gap-0.5">
              {['bg-white/5', 'bg-primary-gold/20', 'bg-primary-gold/40', 'bg-primary-gold/60', 'bg-primary-gold/90'].map((c, i) => (
                <div key={i} className={`w-2.5 h-2.5 rounded-sm ${c}`}></div>
              ))}
            </div>
            <span className="text-[9px] text-purple-400/50">많음</span>
          </div>
        </div>
      </div>
    </section>
  );
}
